import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useAuthTranslation, useValidationTranslation } from '@/i18n/hooks/useTypedTranslation';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { RegisterDto } from '@/types';

function getPasswordStrength(password: string): {
  score: number;
  labelKey: 'weak' | 'fair' | 'good' | 'strong';
  color: string;
} {
  let score = 0;

  if (password.length >= 8) score += 25;
  if (password.length >= 12) score += 10;
  if (/[a-z]/.test(password)) score += 15;
  if (/[A-Z]/.test(password)) score += 15;
  if (/[0-9]/.test(password)) score += 15;
  if (/[^a-zA-Z0-9]/.test(password)) score += 20;

  if (score < 30) return { score, labelKey: 'weak', color: '#ef4444' };
  if (score < 60) return { score, labelKey: 'fair', color: '#f59e0b' };
  if (score < 80) return { score, labelKey: 'good', color: '#14B8A6' };
  return { score, labelKey: 'strong', color: '#10b981' };
}

export function Register() {
  const { register: registerUser } = useAuth();
  const { success } = useToast();
  const navigate = useNavigate();
  const { t } = useAuthTranslation();
  const { t: tValidation } = useValidationTranslation();
  const [error, setError] = useState<string | null>(null);
  const [backendErrors, setBackendErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterDto>();

  const password = watch('password', '');
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  const onSubmit = async (data: RegisterDto) => {
    setLoading(true);
    setError(null);
    setBackendErrors([]);
    try {
      await registerUser({
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        fullName: data.fullName,
      });
      success(t('register.success.message'));
      navigate('/dashboard');
    } catch (err: unknown) {
      // Check if it's an axios error with validation errors
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { errors?: string[] } } };
        const errors = axiosError.response?.data?.errors;
        if (errors && Array.isArray(errors) && errors.length > 0) {
          setBackendErrors(errors);
        } else {
          setError(t('login.errors.invalidCredentials'));
        }
      } else {
        setError(t('login.errors.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
          }}
        >
          {t('register.title')}
        </Typography>
      </Box>

      {/* Form Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 3.5 },
          borderRadius: { xs: 3, sm: 4 },
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgb(20 184 166 / 0.08)',
            borderColor: (theme) => theme.palette.mode === 'light'
              ? 'rgba(20, 184, 166, 0.15)'
              : 'rgba(20, 184, 166, 0.3)',
          },
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {backendErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {backendErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </Box>
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t('register.fullName.label')}
            placeholder={t('register.fullName.placeholder')}
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            {...register('fullName', { required: tValidation('name.required') })}
          />

          <TextField
            fullWidth
            label={t('register.email.label')}
            type="email"
            placeholder={t('register.email.placeholder')}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            {...register('email', {
              required: tValidation('email.required'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: tValidation('email.invalid'),
              },
            })}
          />

          <TextField
            fullWidth
            label={t('register.password.label')}
            type={showPassword ? 'text' : 'password'}
            placeholder={t('register.password.placeholder')}
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <VisibilityOffIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...register('password', {
              required: tValidation('password.required'),
              minLength: {
                value: 8,
                message: tValidation('password.minLength', { min: 8 }),
              },
              maxLength: {
                value: 128,
                message: tValidation('password.maxLength', { max: 128 }),
              },
              validate: {
                hasUppercase: (value) => /[A-Z]/.test(value) || tValidation('password.uppercase'),
                hasLowercase: (value) => /[a-z]/.test(value) || tValidation('password.lowercase'),
                hasNumber: (value) => /[0-9]/.test(value) || tValidation('password.number'),
                hasSpecial: (value) => /[^a-zA-Z0-9]/.test(value) || tValidation('password.special'),
              },
            })}
          />

          {/* Password Strength Indicator - Minimal */}
          {password && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha('#14B8A6', 0.04),
                mb: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {t('register.passwordStrength.title')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: passwordStrength.color,
                    fontSize: '0.75rem',
                  }}
                >
                  {t(`register.passwordStrength.${passwordStrength.labelKey}`)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={passwordStrength.score}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: alpha(passwordStrength.color, 0.2),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: passwordStrength.color,
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                }}
              />
            </Box>
          )}

          {!password && <Box sx={{ mb: 2.5 }} />}

          <TextField
            fullWidth
            label={t('register.confirmPassword.label')}
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={t('register.confirmPassword.placeholder')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...register('confirmPassword', {
              required: tValidation('password.required'),
              validate: (value) => value === password || tValidation('password.match'),
            })}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '10px',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(20, 184, 166, 0.35)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                {t('register.submitting')}
              </>
            ) : (
              t('register.submit')
            )}
          </Button>
        </Box>
      </Paper>

      {/* Sign In Link */}
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
        {t('register.hasAccount')}{' '}
        <Typography
          component={Link}
          to="/login"
          variant="body2"
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            fontWeight: 600,
            transition: 'color 0.2s ease',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.dark',
            },
          }}
        >
          {t('register.signIn')}
        </Typography>
      </Typography>
    </Box>
  );
}

export default Register;
